import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  MenuItem,
  Autocomplete,
  Chip,
  Tooltip,
} from "@mui/material";

interface Account {
  id: number;
  name: string;
  slug: string;

  description?: string;
  notes?: string;

  type: string;
  status: string;
  visibility: string;

  tags: string[];

  email?: string;
  phone?: string;
  website?: string;
  domain?: string;

  address?: string;
  city?: string;
  country?: string;

  workspaceId: number;
  linkedUserId?: number;

  createdAt: string;
  updatedAt: string;
}

const TYPE_OPTIONS = ["ORGANIZATION", "PERSON", "PARTNER", "VENDOR", "INTERNAL"] as const;
const STATUS_OPTIONS = ["ACTIVE", "PROSPECT", "DORMANT", "CHURNED", "ARCHIVED"] as const;
const VISIBILITY_OPTIONS = ["WORKSPACE", "DEPARTMENT"] as const;

type Props = {
  open: boolean;
  mode: "create" | "edit";
  initial?: Account | null;
  workspaceId?: number; // required for create
  onClose: () => void;
  onSave: (payload: Record<string, any>) => Promise<void>;
};

const AccountFormDialog: React.FC<Props> = ({
  open,
  mode,
  initial,
  workspaceId,
  onClose,
  onSave,
}) => {
  const isEdit = mode === "edit";

  const [name, setName] = useState("");
  const [type, setType] = useState<(typeof TYPE_OPTIONS)[number]>("ORGANIZATION");
  const [status, setStatus] = useState<(typeof STATUS_OPTIONS)[number]>("ACTIVE");
  const [visibility, setVisibility] =
    useState<(typeof VISIBILITY_OPTIONS)[number]>("WORKSPACE");
  const [tags, setTags] = useState<string[]>([]);

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [domain, setDomain] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");

  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [linkedUserId, setLinkedUserId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (isEdit && initial) {
      setName(initial.name || "");
      setType((initial.type as any) || "ORGANIZATION");
      setStatus((initial.status as any) || "ACTIVE");
      setVisibility((initial.visibility as any) || "WORKSPACE");
      setTags(initial.tags || []);
      setEmail(initial.email || "");
      setPhone(initial.phone || "");
      setWebsite(initial.website || "");
      setDomain(initial.domain || "");
      setAddress(initial.address || "");
      setCity(initial.city || "");
      setCountry(initial.country || "");
      setDescription(initial.description || "");
      setNotes(initial.notes || "");
      setLinkedUserId(initial.linkedUserId);
    } else if (!isEdit) {
      setName("");
      setType("ORGANIZATION");
      setStatus("ACTIVE");
      setVisibility("WORKSPACE");
      setTags([]);
      setEmail("");
      setPhone("");
      setWebsite("");
      setDomain("");
      setAddress("");
      setCity("");
      setCountry("");
      setDescription("");
      setNotes("");
      setLinkedUserId(undefined);
    }
  }, [isEdit, initial, open]);

  const handleSubmit = async () => {
    if (!name.trim()) return;

    // light validation for contact fields
    const emailSafe = email.trim();
    const websiteSafe = website.trim();
    const domainSafe = domain.trim();

    const payload: Record<string, any> = {
      name: name.trim(),
      type,
      status,
      visibility,
      tags,
      email: emailSafe || undefined,
      phone: phone.trim() || undefined,
      website: websiteSafe || undefined,
      domain: domainSafe || undefined,
      address: address.trim() || undefined,
      city: city.trim() || undefined,
      country: country.trim() || undefined,
      description: description.trim() || undefined,
      notes: notes.trim() || undefined,
      linkedUserId: linkedUserId ?? undefined,
    };
    if (!isEdit) payload.workspaceId = workspaceId;

    await onSave(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? "Edit Account" : "New Account"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            helperText="Human-friendly name. Slug is generated server-side."
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%" }}>
            <TextField select label="Type" value={type} onChange={(e) => setType(e.target.value as any)} fullWidth>
              {TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>

            <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value as any)} fullWidth>
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Visibility"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as any)}
              fullWidth
              helperText="WORKSPACE is visible to all in workspace; DEPARTMENT for scoped usage."
            >
              {VISIBILITY_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Autocomplete
            multiple
            freeSolo
            value={tags}
            onChange={(_, v) => setTags(v as string[])}
            options={[]}
            renderTags={(value: string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option + index} />
              ))
            }
            renderInput={(params) => <TextField {...params} label="Tags" placeholder="Type and press Enter" />}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%" }}>
            <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
            <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%" }}>
            <TextField
              label="Website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              fullWidth
              placeholder="https://example.com"
            />
            <TextField label="Domain" value={domain} onChange={(e) => setDomain(e.target.value)} fullWidth placeholder="example.com" />
          </Stack>

          <TextField label="Address" value={address} onChange={(e) => setAddress(e.target.value)} fullWidth />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%" }}>
            <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} fullWidth />
            <TextField
              label="Country (ISO-2)"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              fullWidth
              placeholder="US, GB, FR…"
            />
          </Stack>

          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline minRows={2} />
          <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} />

          <Tooltip title="Optional link to an existing app user. Each user can be linked to at most one account.">
            <TextField
              label="Linked User ID (optional)"
              type="number"
              value={linkedUserId ?? ""}
              onChange={(e) => setLinkedUserId(e.target.value === "" ? undefined : Number(e.target.value))}
              fullWidth
            />
          </Tooltip>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" onClick={async () => { await handleSubmit(); onClose(); }}>
          {isEdit ? "Save changes" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountFormDialog;
